import { Env } from '@env';
import { format } from 'date-fns';
import { Image } from 'expo-image';
import { router, useLocalSearchParams } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useCar } from '@/api/partners/cars/use-car';
import { useCarStateRefresh } from '@/api/partners/cars/use-car-state-refresh';
import { useLockCar } from '@/api/partners/cars/use-lock-car';
import { useUnlockCar } from '@/api/partners/cars/use-unlock-car';
import { useUpdateCar } from '@/api/partners/cars/use-update-car';
import { MapView } from '@/components/map-view';
import { camelCaseToTitleCase, getCarImages, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Icon } from '@/ui/icon';
import { Lock } from '@/ui/icons';
import { Car as CarIcon } from '@/ui/icons';
import { ArrowClockwise } from '@/ui/icons/arrow-clockwise';
import { Battery } from '@/ui/icons/battery';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { Dashboard } from '@/ui/icons/dashboard';
import { Unlocked } from '@/ui/icons/unlocked';
import { Loader } from '@/ui/loader';

import { SetupDigitalKeyModal } from '../cars/setup-digital-key-modal';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';
import { BlockCarModal } from './block-car-modal';
import CarDetailsForm from './car-details-form';
import PricingForm from './pricing-form';
import RentalCalendar from './rental-calendar';
import CarSubscriptionForm from './subscription-form';
import { UnlistCarModal } from './unlist-car-modal';

enum Tab {
  PRICING = 'Pricing & Availability',
  DETAILS = 'Car Details',
  SUBSCRIPTIONS = 'Subscriptions',
  RENTAL_CALENDAR = 'Rental Calendar',
}

export default function EditCar() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const status = useAuth.use.status();
  const [showUnlistCarModal, setShowUnlistCarModal] = useState(false);
  const [showBlockCarModal, setShowBlockCarModal] = useState(false);
  const [showSetupDigitalKeyModal, setShowSetupDigitalKeyModal] =
    useState(false);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.PRICING);
  const [formValues, setFormValues] = useState<Record<number, any>>({});
  const [formValidity, setFormValidity] = useState<Record<number, boolean>>({});
  const [formErrors, setFormErrors] = useState<Record<number, any>>({});
  const keyboardScrollRef = useRef<KeyboardAwareScrollView>(null);
  const viewRef = useRef<View>(null);
  const invalidTopRef = useRef<number | null>(null);

  const {
    data: carData,
    isFetching: isFetchingCar,
    refetch: refetchCar,
  } = useCar({
    variables: {
      id: Number(local.id),
    },
    enabled: !!local.id,
  });

  const { data: customer } = useCustomer();

  const { mutateAsync: unlockCar, isPending: isUnlockingCar } = useUnlockCar();

  const { mutateAsync: lockCar, isPending: isLockingCar } = useLockCar();

  const { mutateAsync: refreshCarState, isPending: isRefreshingCarState } =
    useCarStateRefresh();

  const { mutateAsync: updateCar, isPending: isUpdatingCar } = useUpdateCar();

  useEffect(() => {
    if (status === 'signIn') {
      refetchCar();
    }
  }, [status]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Edit Car',
    });
  }, []);

  const renderStatus = useMemo(() => {
    if (!carData) return '';

    if (
      (carData.isStandardFleetConnected &&
        carData.standardFleetVirtualKeyConnected) ||
      carData.isReslaCar ||
      carData.isOffline
    ) {
      return 'Ready';
    }

    if (!carData.isStandardFleetConnected) {
      return 'Disconnected';
    }

    return 'Add Digital Key';
  }, [carData]);

  const isKeyConnected =
    !!carData?.isStandardFleetConnected &&
    !!carData.standardFleetVirtualKeyConnected &&
    !carData.isReslaCar &&
    !carData.isOffline;

  const handleFormChange = (index: string, values: any) => {
    setFormValues((prev) => ({ ...prev, [index]: values }));
  };

  const handleValidChange = (index: string, isValid: boolean) => {
    setFormValidity((prev) => ({ ...prev, [index]: isValid }));
  };

  const handleErrorsChange = (index: string, errors: any) => {
    setFormErrors((prev) => ({ ...prev, [index]: errors }));
  };

  const handleSave = async () => {
    const invalid = Object.values(formValidity).filter(
      (isValid) => isValid === false
    );

    if (invalid.length > 0) {
      // const y = invalidTopRef.current ?? 0;
      // keyboardScrollRef.current?.scrollToPosition(0, Math.max(0, y - 20), true);
      return;
    }

    const values = Object.values(formValues).reduce(
      (acc, obj) => ({ ...acc, ...obj }),
      {}
    );

    console.log('values', values);

    try {
      await updateCar({
        id: carData?.id,
        ...values,
      });

      Toast.show({
        text1: 'Car updated successfully',
        type: 'success',
      });

      if (carData!.softDelete || !carData!.isApproved) {
        mixpanelService.track('Car Relisted', {
          'Car VIN': carData!.vin,
        });
      }

      refetchCar();
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleUnlock = async () => {
    try {
      await unlockCar({
        id: carData?.id as number,
      });

      refetchCar();

      Toast.show({
        text1: 'Car unlocked successfully',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleLock = async () => {
    try {
      await lockCar({
        id: carData?.id as number,
      });

      refetchCar();

      Toast.show({
        text1: 'Car locked successfully',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleRefresh = async () => {
    try {
      await refreshCarState({
        id: carData?.id as number,
      });

      refetchCar();

      Toast.show({
        text1: 'Car state refreshed successfully',
        type: 'success',
      });
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleConnectCars = async () => {
    const baseUrl = `${Env.STANDARD_FLEET_URI}&redirect_uri=${Env.STANDARD_FLEET_REDIRECT_URI}&partner_ref=${customer?.partnerToken}`;

    if (Platform.OS === 'web') {
      window.open(baseUrl, '_blank');
    } else {
      await WebBrowser.openBrowserAsync(baseUrl);
    }
  };

  if (
    !carData ||
    isUnlockingCar ||
    isLockingCar ||
    isRefreshingCarState ||
    isUpdatingCar
  ) {
    return (
      <View style={{ flex: 1, backgroundColor: theme.colors.background_1 }}>
        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      <KeyboardAwareScrollView
        ref={keyboardScrollRef}
        style={styles.scrollView}
        contentContainerStyle={{
          gap: 16,
          paddingBottom: 100,
        }}
        keyboardShouldPersistTaps="always"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isFetchingCar}
            onRefresh={() => {
              refetchCar();
            }}
          />
        }
      >
        <View style={styles.nav}>
          <NavigationButton
            type={NavigationButtonTypes.back_with_text}
            onPress={() => {
              router.navigate('/partner/cars');
            }}
          />
        </View>

        {carData.isApproved &&
        !carData.isReslaCar &&
        !carData.isOffline &&
        carData.isStandardFleetConnected &&
        carData.standardFleetUserAccessType === 'DRIVER' ? (
          <View style={styles.alertContainer}>
            <Alert
              type={AlertType.WARNING}
              title="This car is connected with a driver account"
              body="Supercharging and full connectivity are off for this car. Sign in with the car's owner account to restore it."
              buttonText="Reconnect"
              onPress={handleConnectCars}
            />
          </View>
        ) : null}

        {carData.isApproved &&
        !carData.isReslaCar &&
        !carData.isOffline &&
        carData.isStandardFleetConnected &&
        !carData.standardFleetVirtualKeyConnected ? (
          <View style={styles.alertContainer}>
            <Alert
              type={AlertType.WARNING}
              title="Digital Key Disconnected."
              buttonText="Set Up Digital Key"
              onPress={() => {
                setShowSetupDigitalKeyModal(true);
              }}
            />
          </View>
        ) : null}

        {carData.isApproved &&
        !carData.isReslaCar &&
        !carData.isOffline &&
        !carData.isStandardFleetConnected ? (
          <View style={styles.alertContainer}>
            <Alert
              type={AlertType.WARNING}
              title="Car Disconnected"
              body="Some features like tolls and charging records are unavailable until the car reconnects."
              buttonText="Fix Issue"
              onPress={handleConnectCars}
            />
          </View>
        ) : null}

        {carData.softDelete || !carData.isApproved ? (
          <View style={styles.alertContainer}>
            <Alert
              type={AlertType.WARNING}
              title="Complete Your Car Setup"
              body="Fill in your car details and hit the 'List car' button below to finish."
            />
          </View>
        ) : null}

        <View style={styles.topContainer}>
          <View style={styles.carCardContainer}>
            <View>
              {carData.vehicleYear && carData.brandName && carData.modelName ? (
                <Text type="title1">{`${carData.vehicleYear} ${carData.brandName} ${carData.modelName}`}</Text>
              ) : (
                <Text type="title1">Loading Car Information...</Text>
              )}

              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <Text
                  type="caption1"
                  style={{
                    color:
                      renderStatus === 'Ready'
                        ? theme.colors.success
                        : renderStatus === 'Disconnected'
                        ? theme.colors.error
                        : theme.colors.warning,
                  }}
                >
                  {renderStatus}
                </Text>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary50 }}
                >
                  |
                </Text>

                <Text
                  type="caption1"
                  style={{ color: theme.colors.text.secondary }}
                >
                  {carData.vin}
                </Text>

                {carData.licensePlate ? (
                  <>
                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary50 }}
                    >
                      |
                    </Text>

                    <Text
                      type="caption1"
                      style={{ color: theme.colors.text.secondary }}
                    >
                      {carData.licensePlate}
                    </Text>
                  </>
                ) : null}
              </View>
            </View>

            <View style={styles.carImageContainer}>
              <Image
                source={{
                  uri:
                    carData && getCarImages(carData.images, 'large').length > 0
                      ? getCarImages(carData.images)[0]?.url
                      : '',
                }}
                style={styles.carImage}
                contentFit="contain"
              />
            </View>
          </View>

          <View style={styles.carControlPanelContainer}>
            <View style={styles.mileageBatteryContainer}>
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <Icon
                  type="dressed"
                  icon={<Dashboard />}
                  fillColor={theme.colors.icon.color}
                  width={22}
                  height={22}
                />

                <View>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Mileage
                  </Text>

                  <Text type="bodyRegular">
                    {carData.carState.odometer} miles
                  </Text>
                </View>
              </View>

              <View
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
              >
                <Icon
                  type="dressed"
                  icon={<Battery />}
                  fillColor={theme.colors.success}
                  width={22}
                  height={22}
                />

                <View>
                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    Battery Level
                  </Text>

                  <Text type="bodyRegular">
                    {carData.carState.usableBatteryLevel}%
                  </Text>
                </View>
              </View>
            </View>

            <View
              style={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 16,
              }}
            >
              <Pressable
                style={{ flex: 1 }}
                onPress={handleUnlock}
                disabled={!isKeyConnected}
              >
                <Card
                  style={[
                    {
                      flex: 1,
                      paddingLeft: 8,
                      paddingRight: 8,
                      paddingTop: 16,
                      paddingBottom: 16,
                      justiftyContent: 'center',
                      alignItems: 'center',
                      gap: 8,
                    },
                    !isKeyConnected && theme.colors.card.disabled,
                  ]}
                >
                  <Icon
                    type="dressed"
                    icon={<Unlocked />}
                    fillColor={
                      isKeyConnected
                        ? theme.colors.icon.color
                        : theme.colors.text.secondary50
                    }
                    width={22}
                    height={22}
                  />

                  <Text
                    type="bodyRegular"
                    style={
                      !isKeyConnected && {
                        color: theme.colors.text.secondary50,
                      }
                    }
                  >
                    Unlock
                  </Text>
                </Card>
              </Pressable>

              <Pressable
                style={{ flex: 1 }}
                onPress={handleLock}
                disabled={!isKeyConnected}
              >
                <Card
                  style={[
                    {
                      flex: 1,
                      paddingLeft: 8,
                      paddingRight: 8,
                      paddingTop: 16,
                      paddingBottom: 16,
                      justiftyContent: 'center',
                      alignItems: 'center',
                      gap: 8,
                    },
                    !isKeyConnected && theme.colors.card.disabled,
                  ]}
                >
                  <Icon
                    type="dressed"
                    icon={<Lock />}
                    fillColor={
                      isKeyConnected
                        ? theme.colors.icon.color
                        : theme.colors.text.secondary50
                    }
                    width={22}
                    height={22}
                  />

                  <Text
                    type="bodyRegular"
                    style={
                      !isKeyConnected && {
                        color: theme.colors.text.secondary50,
                      }
                    }
                  >
                    Lock
                  </Text>
                </Card>
              </Pressable>

              <Pressable
                style={{ flex: 1 }}
                onPress={() => {
                  setShowBlockCarModal(true);
                }}
              >
                <Card
                  style={{
                    paddingLeft: 8,
                    paddingRight: 8,
                    paddingTop: 16,
                    paddingBottom: 16,
                    justiftyContent: 'center',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <Icon
                    type="dressed"
                    icon={<CalendarIcon />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />

                  <Text type="bodyRegular">Block</Text>
                </Card>
              </Pressable>
            </View>

            <View style={styles.mapContainer}>
              <MapView
                markers={[
                  {
                    latitude: carData.carState.lat,
                    longitude: carData.carState.lon,
                    icon: (
                      <Icon
                        type="naked"
                        icon={<CarIcon />}
                        fillColor={theme.colors.icon.color}
                        width={14}
                        height={14}
                      />
                    ),
                    active: true,
                  },
                ]}
                defaultCenter={{
                  latitude: carData.carState.lat,
                  longitude: carData.carState.lon,
                }}
                fitBounds={false}
                scrollEnabled={false}
                altitude={3000}
                zoom={16}
                showsUserLocation={false}
                style={{ borderRadius: 16 }}
              />
            </View>
          </View>
        </View>

        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          <Button
            text="Refresh"
            type="ghost"
            onPress={handleRefresh}
            leftIcon={
              <Icon
                type="naked"
                icon={<ArrowClockwise />}
                fillColor={theme.colors.icon.color}
                width={14}
                height={14}
              />
            }
            style={{
              width: 100,
              paddingLeft: 0,
              paddingRight: 0,
            }}
          />

          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            Last updated: {format(carData?.carState.since, 'MMM d | h:mm a')}
          </Text>
        </View>

        <View
          ref={viewRef}
          onLayout={(e) => {
            invalidTopRef.current = e.nativeEvent.layout.y;
          }}
        >
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={{
              width: '100%',
              padding: 16,
              borderTopWidth: 1,
              borderTopColor: theme.colors.card.default.borderColor,
              borderBottomWidth: 1,
              borderBottomColor: theme.colors.card.default.borderColor,
            }}
            contentContainerStyle={{
              flexDirection: 'row',
              gap: 8,
              paddingRight: 50,
            }}
          >
            <Button
              text="Pricing & Availability"
              type={
                activeTab === Tab.PRICING ? 'primarySmall' : 'secondarySmall'
              }
              onPress={() => {
                setActiveTab(Tab.PRICING);
              }}
              style={{ width: 200 }}
            />

            <Button
              text="Car Details"
              type={
                activeTab === Tab.DETAILS ? 'primarySmall' : 'secondarySmall'
              }
              onPress={() => {
                setActiveTab(Tab.DETAILS);
              }}
              style={{ width: 125 }}
            />

            <Button
              text="Subscription"
              type={
                activeTab === Tab.SUBSCRIPTIONS
                  ? 'primarySmall'
                  : 'secondarySmall'
              }
              onPress={() => {
                setActiveTab(Tab.SUBSCRIPTIONS);
              }}
              style={{ width: 150 }}
            />

            {Platform.OS === 'web' ? (
              <Button
                text="Rental Calendar"
                type={
                  activeTab === Tab.RENTAL_CALENDAR
                    ? 'primarySmall'
                    : 'secondarySmall'
                }
                onPress={() => {
                  setActiveTab(Tab.RENTAL_CALENDAR);
                }}
                style={{ width: 175 }}
              />
            ) : null}
          </ScrollView>
        </View>

        <View style={{ display: activeTab === Tab.PRICING ? 'flex' : 'none' }}>
          <PricingForm
            car={carData}
            name="pricing-form"
            onChange={handleFormChange}
            onValidChange={handleValidChange}
            onErrorsChange={handleErrorsChange}
          />
        </View>

        <View style={{ display: activeTab === Tab.DETAILS ? 'flex' : 'none' }}>
          <CarDetailsForm
            car={carData}
            name="car-details-form"
            onChange={handleFormChange}
            onValidChange={handleValidChange}
            onErrorsChange={handleErrorsChange}
          />
        </View>

        <View
          style={{ display: activeTab === Tab.SUBSCRIPTIONS ? 'flex' : 'none' }}
        >
          <CarSubscriptionForm
            car={carData}
            name="subscription-form"
            onChange={handleFormChange}
            onValidChange={handleValidChange}
          />
        </View>

        {Platform.OS === 'web' ? (
          <View
            style={{
              display: activeTab === Tab.RENTAL_CALENDAR ? 'flex' : 'none',
            }}
          >
            <RentalCalendar car={carData} />
          </View>
        ) : null}

        <View style={styles.buttonsContainer}>
          {!carData.softDelete ? (
            <Button
              text={'Unlist Car'}
              type="ghost"
              onPress={() => {
                setShowUnlistCarModal(true);
              }}
              style={{ width: 130, paddingLeft: 0 }}
              textStyle={{
                color: carData.softDelete
                  ? theme.colors.success
                  : theme.colors.error,
              }}
            />
          ) : (
            <View />
          )}

          <Button
            text={
              carData.softDelete || !carData.isApproved ? 'List Car' : 'Save'
            }
            type="primary"
            onPress={handleSave}
            style={styles.saveButton}
            disabled={
              isUpdatingCar ||
              Object.values(formValidity).filter((isValid) => isValid === false)
                .length > 0
            }
          />
        </View>

        <View style={styles.errorsContainer}>
          {Object.values(formErrors).map((errors, index) =>
            Object.keys(errors || {}).map((field) => (
              <Text
                key={`${index}-${field}`}
                type="caption1"
                style={{ color: theme.colors.error }}
              >
                {/* {`${camelCaseToTitleCase(field)} ${errors[field]?.message}`} */}
                {`${camelCaseToTitleCase(field)} is invalid`}
              </Text>
            ))
          )}
        </View>
      </KeyboardAwareScrollView>

      <BlockCarModal
        visible={showBlockCarModal}
        car={carData}
        onClose={() => {
          setShowBlockCarModal(false);
        }}
        onSuccess={() => {
          Toast.show({
            text1: 'Car blocked successfully',
            type: 'success',
          });
          setShowBlockCarModal(false);
        }}
      />

      <UnlistCarModal
        visible={showUnlistCarModal}
        car={carData}
        onClose={() => {
          setShowUnlistCarModal(false);
        }}
        onSuccess={() => {
          refetchCar();
          setShowUnlistCarModal(false);
          Toast.show({
            text1: 'Car unlisted successfully',
            type: 'success',
          });
        }}
      />

      <SetupDigitalKeyModal
        visible={showSetupDigitalKeyModal}
        onClose={() => setShowSetupDigitalKeyModal(false)}
        car={carData}
        onVerifyKeySuccess={() => {
          refetchCar();
        }}
      />
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  scrollView: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 0,
      md: 56,
    },
    paddingRight: {
      xs: 0,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  alertContainer: {
    paddingLeft: {
      xs: 16,
      md: 0,
    },
    paddingRight: {
      xs: 16,
      md: 0,
    },
  },
  topContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    gap: 16,
  },
  carCardContainer: {
    flex: 1,
    backgroundColor: {
      md: theme.colors.card.default.backgroundColor,
    },
    borderRadius: {
      md: 16,
    },
    padding: 16,
  },
  carImageContainer: {
    flex: 1,
    minHeight: {
      xs: 250,
      md: 400,
    },
  },
  carImage: {
    width: {
      xs: '100%',
      md: '100%',
    },
    height: {
      xs: 250,
      md: 400,
    },
  },
  carControlPanelContainer: {
    flex: 1,
    flexDirection: 'column',
    gap: 16,
    paddingLeft: {
      xs: 16,
      md: 0,
    },
    paddingRight: {
      xs: 16,
      md: 0,
    },
  },
  mileageBatteryContainer: {
    flexDirection: 'row',
    gap: 16,
    paddingLeft: {
      md: 16,
    },
    paddingRight: {
      md: 16,
    },
    paddingTop: {
      md: 16,
    },
    paddingBottom: {
      md: 16,
    },
    borderRadius: {
      md: 16,
    },
    backgroundColor: {
      md: theme.colors.card.default.backgroundColor,
    },
  },
  mapContainer: {
    height: {
      xs: 150,
      md: 'auto',
    },
    flex: {
      md: 1,
    },
    width: '100%',
    borderRadius: 16,
    overflow: 'hidden',
  },
  buttonsContainer: {
    gap: 16,
    paddingLeft: {
      xs: 16,
      md: 0,
    },
    paddingRight: {
      xs: 16,
      md: 0,
    },
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    justifyContent: {
      md: 'space-between',
    },
  },
  saveButton: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  errorsContainer: {
    alignItems: {
      md: 'flex-end',
    },
    paddingLeft: {
      xs: 16,
      md: 0,
    },
  },
}));
